import { cn } from "@/lib/utils";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";

export const Card = ({ className, children, hover, ...p }) => (
  <div
    className={cn(
      "bg-white border border-[#E5E7EB] rounded-xl",
      hover && "card-hover cursor-pointer",
      className
    )}
    {...p}
  >
    {children}
  </div>
);

export const Overline = ({ children, className }) => (
  <div className={cn("overline", className)}>{children}</div>
);

export const SectionTitle = ({ children, sub, className, right }) => (
  <div className={cn("flex items-end justify-between mb-5", className)}>
    <div>
      <h2 className="font-display text-2xl font-semibold tracking-tight text-[#1C1F1E]">{children}</h2>
      {sub && <p className="text-sm text-[#52525B] mt-1">{sub}</p>}
    </div>
    {right}
  </div>
);

export const Delta = ({ value, positive, className }) => (
  <span
    className={cn(
      "inline-flex items-center gap-1 text-xs font-semibold",
      positive ? "text-[#059669]" : "text-[#DC2626]",
      className
    )}
  >
    {positive ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
    {value}
  </span>
);

export function KpiCard({ label, value, delta, positive, sub, testid }) {
  return (
    <Card hover className="p-6 fade-up" data-testid={testid}>
      <Overline>{label}</Overline>
      <div className="mt-3 flex items-baseline gap-3">
        <span className="font-display text-3xl font-semibold tracking-tight text-[#1C1F1E] font-mono2">
          {value}
        </span>
      </div>
      <div className="mt-2 flex items-center gap-2">
        {delta && (positive === undefined ? (
          <span className="text-xs font-medium text-[#52525B]">{delta}</span>
        ) : (
          <Delta value={delta} positive={positive} />
        ))}
        {sub && <span className="text-xs text-[#A1A1AA]">{sub}</span>}
      </div>
    </Card>
  );
}

const STATUS_STYLES = {
  Available: "border-[#D1D5DB] text-[#71717A] bg-white",
  Reserved: "border-[#2563EB]/30 text-[#2563EB] bg-[#2563EB]/5",
  "Checked In": "border-[#C7A24A]/40 text-[#B08423] bg-[#C7A24A]/10",
  "On Course": "border-[#059669]/30 text-[#059669] bg-[#059669]/5",
  Completed: "border-[#A1A1AA]/40 text-[#52525B] bg-[#F3F4F6]",
  Healthy: "border-[#059669]/30 text-[#059669] bg-[#059669]/5",
  "Low Stock": "border-[#D97706]/40 text-[#D97706] bg-[#D97706]/10",
  Reorder: "border-[#DC2626]/30 text-[#DC2626] bg-[#DC2626]/5",
  "On Pace": "border-[#059669]/30 text-[#059669] bg-[#059669]/5",
  "Minor Delay": "border-[#D97706]/40 text-[#D97706] bg-[#D97706]/10",
  "Major Bottleneck": "border-[#DC2626]/30 text-[#DC2626] bg-[#DC2626]/5",
  Watch: "border-[#D97706]/40 text-[#D97706] bg-[#D97706]/10",
  Behind: "border-[#DC2626]/30 text-[#DC2626] bg-[#DC2626]/5",
  Critical: "border-[#DC2626]/30 text-[#DC2626] bg-[#DC2626]/5",
  "No Show": "border-[#DC2626]/30 text-[#DC2626] bg-[#DC2626]/5",
  Paid: "border-[#059669]/30 text-[#059669] bg-[#059669]/5",
  Deposit: "border-[#C7A24A]/40 text-[#B08423] bg-[#C7A24A]/10",
  Unpaid: "border-[#A1A1AA]/40 text-[#71717A] bg-[#F3F4F6]",
};

export const StatusPill = ({ status, className }) => (
  <span
    className={cn(
      "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-xs font-medium whitespace-nowrap",
      STATUS_STYLES[status] || "border-[#D1D5DB] text-[#71717A]",
      className
    )}
  >
    <span className={cn("w-1.5 h-1.5 rounded-full",
      status === "On Course" || status === "On Pace" || status === "Healthy" || status === "Paid" ? "bg-[#059669]" :
      status === "Reserved" ? "bg-[#2563EB]" :
      status === "Checked In" || status === "Deposit" ? "bg-[#C7A24A]" :
      status === "Minor Delay" || status === "Low Stock" || status === "Watch" ? "bg-[#D97706]" :
      status === "Major Bottleneck" || status === "Reorder" || status === "Behind" || status === "Critical" || status === "No Show" ? "bg-[#DC2626]" : "bg-[#A1A1AA]"
    )} />
    {status}
  </span>
);

const SEG_STYLES = {
  VIP: "bg-[#0F3F2D] text-white border-transparent",
  Frequent: "bg-[#2F7A57]/12 text-[#2F7A57] border-[#2F7A57]/25",
  Regular: "bg-[#2563EB]/8 text-[#2563EB] border-[#2563EB]/20",
  Occasional: "bg-[#F3F4F6] text-[#52525B] border-[#E5E7EB]",
  "At Risk": "bg-[#DC2626]/8 text-[#DC2626] border-[#DC2626]/25",
  New: "bg-[#C7A24A]/12 text-[#B08423] border-[#C7A24A]/30",
};

export const SegmentChip = ({ segment, className }) => (
  <span className={cn("inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-semibold",
    SEG_STYLES[segment] || "bg-[#F3F4F6] text-[#52525B]", className)}>
    {segment}
  </span>
);
